/**
 * A pool of projectile meshes drawn as GPU instances of a single master sphere
 * (one draw call regardless of count). Instances are enabled on acquire and
 * disabled on release — never created/destroyed per shot.
 */
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder.js";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial.js";
import { Color3 } from "@babylonjs/core/Maths/math.color.js";
import type { InstancedMesh } from "@babylonjs/core/Meshes/instancedMesh.js";
import type { Scene } from "@babylonjs/core/scene.js";

/** Visual diameter for projectile instances (sim radii are ~0.22). */
const VISUAL_DIAMETER = 0.48;

export interface ProjectilePool {
  acquire(): InstancedMesh | null;
  release(inst: InstancedMesh): void;
}

export const createProjectilePool = (scene: Scene, max = 96): ProjectilePool => {
  const master = MeshBuilder.CreateSphere(
    "projectile",
    { diameter: VISUAL_DIAMETER, segments: 6 },
    scene,
  );
  const mat = new StandardMaterial("projMat", scene);
  mat.emissiveColor = new Color3(1, 0.82, 0.3);
  mat.diffuseColor = new Color3(0.5, 0.4, 0.1);
  mat.disableLighting = true;
  master.material = mat;
  master.isVisible = false; // the master itself is never drawn
  master.isPickable = false;

  const free: InstancedMesh[] = [];
  for (let i = 0; i < max; i++) {
    const inst = master.createInstance(`projectile-${i}`);
    inst.isPickable = false;
    inst.setEnabled(false);
    free.push(inst);
  }

  return {
    acquire(): InstancedMesh | null {
      const inst = free.pop();
      if (!inst) return null; // pool exhausted; shot simply isn't drawn
      inst.setEnabled(true);
      return inst;
    },
    release(inst: InstancedMesh): void {
      inst.setEnabled(false);
      free.push(inst);
    },
  };
};
